import React, { useEffect, CSSProperties } from 'react';
import cx from 'classnames';
import { prefix } from './utils/main';
import { ModalDialog } from './components/dialog';
import { ModalBsSize } from './modal.types';
import { createPortal } from 'react-dom';

import './modal.styles.scss';

type ModalProps = React.PropsWithChildren<{
  bsSize?: ModalBsSize;
  animation?: boolean;
  dialogClassName?: string;
  backdrop?: 'static' | boolean;
  backdropClassName?: string;
  backdropStyle?: CSSProperties;
  show?: boolean;
  onHide?: () => void;
  className?: string;
  style?: CSSProperties;
  bsClass?: string;
  onEscapeKeyDown?: () => void;
}>;

export const Modal: React.FC<ModalProps> = ({
  backdrop = true,
  backdropStyle,
  bsClass,
  backdropClassName,
  show = false,
  onHide,
  className,
  style,
  children,
  bsSize,
  dialogClassName,
}) => {
  
  const rootElRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEsc = async (event: KeyboardEvent) => {
      if (event.key === 'Escape' && onHide) {
        onHide();
      }
    };

    if (show && rootElRef.current) {
      rootElRef.current?.addEventListener('keydown', handleEsc);
      rootElRef.current?.focus();
    }

    return () => {
      rootElRef.current?.removeEventListener('keydown', handleEsc);
      const openedModals = document.querySelectorAll('[data-internal-field-for-focus]');
      (openedModals[openedModals.length - 1] as HTMLDivElement)?.focus();
    };
  }, [show]);


  if (!show) {
    return null;
  }

  const component = (
    <div role="dialog" data-internal-field-for-focus ref={rootElRef} tabIndex={-1}>
      {(backdrop === 'static' || backdrop) && (
        <div
          style={backdropStyle}
          className={cx('Modal__backdrop', prefix({ bsClass }, 'backdrop'), backdropClassName)}
          onClick={backdrop === true ? onHide : undefined}
        />
      )}
      <ModalDialog
        bsSize={bsSize}
        bsClass={bsClass}
        className={cx('Modal', className)}
        dialogClassName={dialogClassName}
        style={style}
      >
        {children}
      </ModalDialog>
    </div>
  );

  return createPortal(
    component,
    document.body
  );
};

